import { useEffect } from 'react';

export interface UseBreakdownChromeOptions {
  isDirty: boolean;
  isSaving: boolean;
  isPreviewMode: boolean;
  onSave: () => void;
  onUndo: () => void;
  onRedo: () => void;
  /**
   * Called whenever `isDirty` changes so a parent (JobDetail) can guard
   * tab switches and back-button navigation.
   */
  onDirtyChange?: (dirty: boolean) => void;
}

/**
 * Browser-level chrome that every breakdown editor needs:
 *   - reports dirty state upward so navigation guards work,
 *   - warns on page close/refresh when there are unsaved edits,
 *   - binds Cmd/Ctrl+S (save), Cmd/Ctrl+Z (undo), Cmd/Ctrl+Shift+Z (redo).
 *
 * Keyboard shortcuts are suppressed while `isPreviewMode` is true — viewing
 * a saved snapshot should not trigger any edits.
 */
export function useBreakdownChrome({
  isDirty,
  isSaving,
  isPreviewMode,
  onSave,
  onUndo,
  onRedo,
  onDirtyChange,
}: UseBreakdownChromeOptions): void {
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) e.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const cmd = e.metaKey || e.ctrlKey;
      if (!cmd) return;
      if (e.key === 's') {
        e.preventDefault();
        if (!isPreviewMode && isDirty && !isSaving) onSave();
      } else if (e.key.toLowerCase() === 'z') {
        // Don't intercept Cmd+Z when the user is editing a text field —
        // they expect the browser's native input undo to run there.
        const tag = (e.target as HTMLElement | null)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        e.preventDefault();
        if (isPreviewMode) return;
        if (e.shiftKey) onRedo();
        else onUndo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDirty, isSaving, isPreviewMode, onSave, onUndo, onRedo]);
}
